import React, { useMemo, useState } from "react";
import { Copy } from "lucide-react";
import SyntaxHighlighter from "react-syntax-highlighter";
import {
  atomOneDark,
  atomOneLight,
} from "react-syntax-highlighter/dist/esm/styles/hljs";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useCopyToClipboard from "./hooks/useCopyToClipboard";
import type { LLMModel } from "./types";
import { normalizeSttResponseFormat } from "./utils/catalog";

type Props = {
  model?: LLMModel | null;
  t: (key: string) => string;
  mode: "light" | "dark";
};

type SnippetType = "curl" | "python";
type ModelType =
  | "chat"
  | "multimodal"
  | "embedding"
  | "tts"
  | "stt"
  | "image"
  | "unknown";

/* ================= HELPERS ================= */

const getModelType = (model?: LLMModel | null): ModelType => {
  if (!model) return "unknown";
  if (model.is_multimodal) return "multimodal";
  if (model.is_chat_model) return "chat";
  if (model.is_embedding_model) return "embedding";
  if (model.is_tts_model) return "tts";
  if (model.is_stt_model) return "stt";
  if (model.is_image_generation_model) return "image";
  return "unknown";
};

const getSnippetModelName = (model: LLMModel): string =>
  model.name?.trim() || model.model?.trim() || "[model-name]";

const buildRequestBody = (model: LLMModel, type: ModelType) => {
  const modelName = getSnippetModelName(model);

  switch (type) {
    case "chat":
    case "multimodal":
      return {
        model: modelName,
        messages: [{ role: "user", content: "Hello!" }],
        ...(model.temperature !== undefined && {
          temperature: model.temperature,
        }),
        ...(model.extra_config?.top_p !== undefined && {
          top_p: Number(model.extra_config.top_p),
        }),
      };

    case "embedding":
      return {
        model: modelName,
        input: "Sample text for embedding",
      };

    case "tts":
      return {
        model: modelName,
        input: "Hello world",
        voice: "<VOICE>",
        response_format: "wav",
      };

    case "image":
      return {
        model: modelName,
        prompt: "A futuristic city",
        size: "1024x1024",
      };

    default:
      return { model: modelName };
  }
};

const generateCurl = (model: LLMModel): string => {
  const type = getModelType(model);
  const endpoint = model.endpoint ?? "";
  const apiKey = "YOUR_API_KEY";
  const modelName = getSnippetModelName(model);

  if (!endpoint) {
    return "# Endpoint not configured";
  }

  if (type === "stt") {
    const responseFormat = normalizeSttResponseFormat(
      model.extra_config?.supported_audio_format,
    );

    return `curl -X POST ${endpoint} \\
  -H "Authorization: Bearer ${apiKey}" \\
  -F "file=@audio.mp3" \\
  -F "model=${modelName}" \\
  -F "response_format=${responseFormat}"`;
  }

  const body = buildRequestBody(model, type);
  const jsonBody = JSON.stringify(body, null, 2).replace(/'/g, "'\\''");
  const outputFlag = type === "tts" ? ` \\
  --output output_speech.wav` : "";

  return `curl -X POST ${endpoint} \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${jsonBody}'${outputFlag}`;
};

const generatePython = (model: LLMModel): string => {
  const type = getModelType(model);
  const modelName = getSnippetModelName(model);

  const base = `
from openai import OpenAI

client = OpenAI()
`;

  switch (type) {
    case "chat":
    case "multimodal":
      return `${base}

response = client.chat.completions.create(
    model="${modelName}",
    messages=[
        {"role": "user", "content": "Hello!"}
    ]
)

print(response.choices[0].message.content)
`;

    case "embedding":
      return `${base}

response = client.embeddings.create(
    model="${modelName}",
    input="Sample text"
)

print(response.data[0].embedding)
`;

    case "tts":
      return `${base}

response = client.audio.speech.create(
    model="${modelName}",
    voice="<VOICE>",
    input="Hello world",
    response_format="wav"
)
`;

    case "stt": {
      const responseFormat = normalizeSttResponseFormat(
        model.extra_config?.supported_audio_format,
      );

      return `${base}

response = client.audio.transcriptions.create(
    model="${modelName}",
    file=open("audio.mp3", "rb"),
    response_format="${responseFormat}"
)

print(response.text)
`;
    }

    case "image":
      return `${base}

response = client.images.generate(
    model="${modelName}",
    prompt="A futuristic city"
)

print(response)
`;

    default:
      return `${base}
res = client.chat.completions.create(
  model="${modelName}",
  messages=[{"role": "user", "content": "Hello!"}]
)`;
  }
};

/* ================= SUB COMPONENT ================= */

type CodeBlockProps = {
  code: string;
  language: "bash" | "python";
  mode: "light" | "dark";
  copyLabel: string;
  onCopy: () => void;
  testId?: string;
};

const CodeBlock: React.FC<CodeBlockProps> = ({
  code,
  language,
  mode,
  copyLabel,
  onCopy,
  testId = "ai-catalog-snippet-copy-button",
}) => {
  return (
    <div className="relative min-h-[202px] min-w-0 max-w-full overflow-hidden [direction:ltr]">
      <Button
        type="button"
        variant="secondary"
        size="icon-sm"
        aria-label={copyLabel}
        data-testid={testId}
        className="absolute right-2 top-2 z-10"
        onClick={onCopy}
      >
        <Copy className="size-4" />
      </Button>

      <SyntaxHighlighter
        language={language}
        style={mode === "dark" ? atomOneDark : atomOneLight}
        wrapLongLines
        customStyle={{
          border: "1px solid var(--border)",
          borderRadius: "10px",
          minHeight: "202px",
          margin: 0,
          padding: "12px",
          paddingInlineEnd: "48px",
          maxWidth: "100%",
          width: "100%",
          backgroundColor: "var(--input)",
          overflowX: "hidden",
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

/* ================= MAIN ================= */

const AICatalogDetailSnippetComp: React.FC<Props> = ({ model, t, mode }) => {
  const [snippetType, setSnippetType] = useState<SnippetType>("curl");
  const copyToClipboard = useCopyToClipboard();

  const snippet = useMemo(() => {
    if (!model) return "";
    return snippetType === "curl" ? generateCurl(model) : generatePython(model);
  }, [model, snippetType]);

  if (!model) return null;

  return (
    <section className="flex min-h-full min-w-0 max-w-full flex-col gap-2 overflow-hidden px-2 py-3">
      <div className="flex items-center justify-between gap-3 px-2">
        <h3 className="text-sm font-medium text-foreground">
          {t("aiCatalog.snippet")}
        </h3>

        <Select
          value={snippetType}
          onValueChange={(value) => setSnippetType(value as SnippetType)}
        >
          <SelectTrigger
            data-testid="ai-catalog-snippet-type-select"
            aria-label={t("aiCatalog.snippet")}
            className="w-[100px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              data-testid="ai-catalog-snippet-type-option-curl"
              value="curl"
            >
              {t("deploymentDetail.curl")}
            </SelectItem>
            <SelectItem
              data-testid="ai-catalog-snippet-type-option-python"
              value="python"
            >
              {t("deploymentDetail.pythonOpenai")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Code */}
      <CodeBlock
        code={snippet}
        language={snippetType === "curl" ? "bash" : "python"}
        mode={mode}
        copyLabel={`${t("common.copy")} ${t("aiCatalog.snippet")}`}
        onCopy={() => copyToClipboard(snippet)}
        testId={`ai-catalog-snippet-copy-${snippetType}`}
      />
    </section>
  );
};

export default AICatalogDetailSnippetComp;
